import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { AbbreviatedDecimal, NoValue } from '~/modules/common/components';

const useStyles = makeStyles(theme => ({
  barColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative'
  },
  bar: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0,
    transition: 'height 0.3s ease'
  },
  valueText: {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    position: 'relative',
    zIndex: 1,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.6rem'
    }
  }
}));

const MAX_BAR_HEIGHT_PX = 32;

const calculateBarHeight = (value, maxValue) => {
  if (!value || !maxValue) return 0;

  return (value / maxValue) * MAX_BAR_HEIGHT_PX;
};

const BarColumn = memo(({ value, maxValue, className, dataQeId }) => {
  const classes = useStyles();
  const height = useMemo(() => calculateBarHeight(value, maxValue), [
    value,
    maxValue
  ]);

  const barStyle = useMemo(() => ({ height }), [height]);

  const barClassName = useMemo(() => classNames(classes.bar, className), [
    classes.bar,
    className
  ]);

  return (
    <Box className={classes.barColumn} aria-hidden="true">
      <Box
        className={barClassName}
        style={barStyle}
        data-testid="bar-column-fill"
      />
      <span className={classes.valueText}>
        {value != null ? (
          <AbbreviatedDecimal
            dataQeId={dataQeId}
            value={value}
            fixedDecimalScale
          />
        ) : (
          <NoValue />
        )}
      </span>
    </Box>
  );
});

BarColumn.propTypes = {
  value: PropTypes.number,
  maxValue: PropTypes.number.isRequired,
  className: PropTypes.string,
  dataQeId: PropTypes.string
};

export default BarColumn;
